// File: frontend/src/components/marketing/HeroSection.js
//
// The hero is a photographic scene filling the whole section, with the headline,
// supporting line, buttons and three facts as real page content on top of it.
//
// ── TWO COMPOSITIONS, NOT ONE CROP ──────────────────────────────────────────
//
// Desktop gets a landscape scene (public/landing/hero-scene-*) whose devices sit
// right of centre, leaving quiet room on the left for the copy. Phones get a
// SEPARATELY COMPOSED portrait scene (hero-scene-portrait-*) whose quiet area is
// at the top, with the devices below it. Centre-cropping the landscape frame to
// portrait would put the copy on top of the laptop, and shrinking it would make
// the product unreadable, so the two are art-directed with `media` on <source>
// rather than picked by width from one srcset. scripts/prerender.js emits one
// preload per AVIF source, carrying the same media query, so each visitor
// preloads only the image they will actually paint.
//
// ── THE HERO IS A DARK BAND IN BOTH THEMES ──────────────────────────────────
//
// The artwork is a dark room, so the copy over it has to be light whatever the
// page theme is. Hero text therefore uses its own fixed values instead of
// text.primary: on the light theme, theme-coloured text would put near-black
// type on a near-black photograph. The section paints INK underneath the image
// for the same reason — the scene fades up from 0.16 (see below), and over the
// light theme's near-white ground that fade would read as a white flash.
//
// ── LCP RULES, MEASURED AND NOT NEGOTIABLE ──────────────────────────────────
//
// The scene is the largest element on the page, so it is the LCP, and the
// measurement lands on whichever paint is LARGEST. Two things wreck it, both
// established on production rather than reasoned about:
//   · any transform on it (translate, scale, rotate) paints it smaller during
//     the intro, so Chrome files a new candidate when the animation settles —
//     ~2.5s instead of ~0.7s. It therefore only fades, and nothing that moves
//     is put on the image or on any of its ancestors.
//   · `opacity: 0` is ignored outright, so the fade would not begin being
//     measured until it reached a visible value. It starts at 0.16.
//
// Motion (see heroIntro.js for why there are two paths):
//   · first paint of the static page: a CSS intro under html[data-intro], which
//     survives React's takeover mid-sequence;
//   · client-side navigation to "/": the same sequence in GSAP.
// Reduced motion: none of it.

import React, { useRef } from "react";
import { Box, Button, Container, Stack, Typography, alpha } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { gsap, useGSAP, MQ } from "./landingMotion";
import { INTRO_DELAY } from "./heroIntro";
import { HERO_SCENE } from "../../theme";

const SUPPORTING = ["Built for hotspot operators", "MTN & Airtel Mobile Money", "No monthly subscription"];

// The scene's ground and ink live in theme.js beside the other fixed brand
// colours: they are the same in both modes because they belong to the
// photograph, not to the page theme. See HERO_SCENE there for why.
const INK = HERO_SCENE.ink;
const HERO_TEXT = HERO_SCENE.text;
const HERO_TEXT_DIM = "rgba(226,235,243,0.80)";

const LANDSCAPE = (ext) => [960, 1440, 1672].map((w) => `/landing/hero-scene-${w}.${ext} ${w}w`).join(", ");
const PORTRAIT = (ext) => [640, 941].map((w) => `/landing/hero-scene-portrait-${w}.${ext} ${w}w`).join(", ");
// The scene is full-bleed, so it is always the width of the viewport.
const SCENE_SIZES = "100vw";
// Both breakpoints carry an explicit media query, and together they cover every
// width. A <source> with no media would be a match at EVERY width, so
// prerender.js — which emits one preload per AVIF source, copying its media —
// would hand phones a preload for the landscape scene as well as the portrait
// one, and they would download both.
const PHONES = "(max-width: 899.95px)";
const WIDER = "(min-width: 900px)";

const rise = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: none; }
`;
// Opacity only, and never from zero. See the LCP note above.
const sceneIn = keyframes`
  from { opacity: 0.16; }
  to   { opacity: 1; }
`;

const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)";

// CSS intro for one hero part. Only under html[data-intro], only for visitors
// who have not asked for reduced motion.
const intro = (part, anim = rise, ms = 560) => ({
  "@media (prefers-reduced-motion: no-preference)": {
    // The negative part of the delay is how far the intro had already got when
    // React replaced this element (index.js sets the variable).
    "html[data-intro] &": { animation: `${anim} ${ms}ms ${EASE_OUT} calc(${INTRO_DELAY[part]}ms - var(--pf-intro-elapsed, 0ms)) both` },
  },
});

export default function HeroSection() {
  const root = useRef(null);

  useGSAP(
    () => {
      // index.js sets this for the first paint of the static page, whose CSS
      // intro continues through the takeover. Consuming it means a later
      // client-side visit to "/" plays the GSAP version instead.
      const cssIntroOwnsIt = window.__pfIntroMode === "css";
      delete window.__pfIntroMode;
      if (cssIntroOwnsIt) return;

      const mm = gsap.matchMedia();
      mm.add({ reduced: MQ.reduced, always: MQ.always }, (ctx) => {
        if (ctx.conditions.reduced) return undefined;
        const q = gsap.utils.selector(root);
        const at = (part) => INTRO_DELAY[part] / 1000;
        const tl = gsap.timeline({ defaults: { ease: "power3.out", duration: 0.56 } });
        // autoAlpha is fine on the copy; the scene gets opacity only, and from
        // 0.16 rather than 0, to match the CSS path exactly.
        tl.from(q('[data-hero="scene"]'), { opacity: 0.16, duration: 0.8 }, at("scene"))
          .from(q('[data-hero="title"]'), { autoAlpha: 0, y: 18 }, at("title"))
          .from(q('[data-hero="copy"]'), { autoAlpha: 0, y: 18 }, at("copy"))
          .from(q('[data-hero="cta"]'), { autoAlpha: 0, y: 18, stagger: 0.06 }, at("cta"))
          .from(q('[data-hero="trust"]'), { autoAlpha: 0, y: 12 }, at("trust"))
          .from(q('[data-hero="caption"]'), { autoAlpha: 0, duration: 0.4 }, at("caption"));
        return undefined;
      });
    },
    { scope: root, dependencies: [] }
  );

  return (
    <Box
      component="section"
      ref={root}
      aria-labelledby="hero-title"
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: INK,
        // Close to the source aspect at the widths that matter, so `cover` trims
        // a little off the top and bottom and never cuts the devices out
        // sideways. Clamped so an ultrawide monitor does not get a 1000px band
        // and a small laptop still has room for the copy.
        minHeight: { xs: "clamp(600px, 168vw, 880px)", md: "clamp(560px, 46vw, 760px)" },
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        pt: { xs: 5, md: 0 },
        pb: { xs: 4, md: 0 },
      }}
    >
      {/* The scene. Absolutely positioned so it cannot inherit a transform from
          a layout ancestor. */}
      <Box
        data-hero="scene"
        sx={{ position: "absolute", inset: 0, zIndex: 0, ...intro("scene", sceneIn, 800) }}
      >
        <Box component="picture" sx={{ display: "block", width: "100%", height: "100%" }}>
          <source media={PHONES} type="image/avif" srcSet={PORTRAIT("avif")} sizes={SCENE_SIZES} />
          <source media={PHONES} type="image/webp" srcSet={PORTRAIT("webp")} sizes={SCENE_SIZES} />
          <source media={WIDER} type="image/avif" srcSet={LANDSCAPE("avif")} sizes={SCENE_SIZES} />
          <source media={WIDER} type="image/webp" srcSet={LANDSCAPE("webp")} sizes={SCENE_SIZES} />
          {/* A plain <img>: on a MUI Box, width/height would become CSS and the
              intrinsic size (no layout shift) would be lost. */}
          <img
            src="/landing/hero-scene-1440.jpg"
            width={1440}
            height={810}
            fetchPriority="high"
            alt="A laptop and a phone on a desk showing the PesaFii dashboard: sample monthly revenue, wallet balance, connected users and a revenue chart"
            style={{ display: "block", width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 58%" }}
          />
        </Box>
      </Box>

      {/* Scrim. Darkens only the side the copy sits on and clears before it
          reaches the devices, so their lighting and detail survive. Horizontal
          on desktop, vertical on phones, matching each composition. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none",
          background: {
            xs: `linear-gradient(180deg, ${alpha(INK, 0.94)} 0%, ${alpha(INK, 0.86)} 26%, ${alpha(INK, 0.52)} 44%, ${alpha(INK, 0.14)} 60%, transparent 74%)`,
            md: `linear-gradient(94deg, ${alpha(INK, 0.95)} 0%, ${alpha(INK, 0.88)} 28%, ${alpha(INK, 0.56)} 46%, ${alpha(INK, 0.16)} 64%, transparent 82%)`,
          },
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2, minWidth: 0, width: "100%" }}>
        <Box sx={{ maxWidth: { xs: "100%", md: 560 } }}>
          <Typography
            id="hero-title"
            component="h1"
            data-hero="title"
            sx={{
              fontSize: "clamp(2rem, 5vw, 3.25rem)", fontWeight: 800, letterSpacing: "-0.03em",
              lineHeight: 1.08, color: HERO_TEXT, mb: 2, textWrap: "balance",
              ...intro("title"),
            }}
          >
            Manage, monetize and grow your{" "}
            {/* "Wi-Fi" never breaks at its hyphen ("Wi-" / "Fi" at 1024px). */}
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>Wi-Fi</Box> network.
          </Typography>

          <Typography
            data-hero="copy"
            sx={{ fontSize: { xs: 15.5, md: 17 }, color: HERO_TEXT_DIM, lineHeight: 1.6, maxWidth: 480, mb: 3.5, ...intro("copy") }}
          >
            Manage hotspot routers, sell vouchers, receive customer payments, monitor users
            and track revenue from one secure dashboard.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 3.5 }}>
            <Button
              data-hero="cta" component={RouterLink} to="/login?mode=register" variant="contained" size="large"
              sx={{
                fontWeight: 700, borderRadius: 2, textTransform: "none", px: 3, py: 1.25,
                "& .arw": { transition: "transform 0.2s" }, "&:hover .arw": { transform: "translateX(3px)" },
                ...intro("cta"),
              }}
            >
              Start Free <Box component="span" className="arw" sx={{ ml: 0.75 }}>→</Box>
            </Button>
            <Button
              data-hero="cta" component="a" href="#product" variant="outlined" size="large"
              sx={{
                fontWeight: 700, borderRadius: 2, textTransform: "none", px: 3, py: 1.25,
                // Fixed light values, not theme tokens: this button sits on the
                // photograph in both themes.
                color: HERO_TEXT,
                borderColor: "rgba(242,246,250,0.34)",
                bgcolor: "rgba(8,12,17,0.34)",
                backdropFilter: "blur(6px)",
                "&:hover": { borderColor: "primary.main", bgcolor: "rgba(8,12,17,0.55)" },
                ...intro("cta"),
              }}
            >
              Explore the Product
            </Button>
          </Stack>

          <Stack
            data-hero="trust" component="ul" direction="row" useFlexGap flexWrap="wrap"
            columnGap={{ xs: 2, sm: 3 }} rowGap={1}
            sx={{ listStyle: "none", m: 0, p: 0, ...intro("trust") }}
          >
            {SUPPORTING.map((s) => (
              <Stack component="li" key={s} direction="row" spacing={0.75} alignItems="center">
                <CheckCircleIcon aria-hidden="true" sx={{ fontSize: 17, color: "primary.main" }} />
                <Typography sx={{ fontSize: 13.5, color: HERO_TEXT_DIM, fontWeight: 600 }}>{s}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Container>

      {/* The screens in the artwork are an illustration, not a live account, and
          the page says so where the artwork is. */}
      <Typography
        data-hero="caption"
        sx={{
          position: "absolute", zIndex: 2, left: 0, right: 0, bottom: { xs: 10, md: 14 },
          textAlign: "center", fontSize: 11, color: "rgba(226,235,243,0.52)",
          ...intro("caption", rise, 400),
        }}
      >
        Illustrative sample data, not a live account
      </Typography>
    </Box>
  );
}
